import { MediaStoryCard } from '@components/display/cards';
import { CenteredEmptyState } from '@components/display/emptyStates';
import { StickyTabs, Tabs } from '@components/utility/tabs';
import { APP_PREFIX_PATH } from '@constants/common';
import { isInfiniteLoadingItem } from '@hooks/useInfiniteLazyLoader';
import { Grid2 } from '@mui/material';
import { useOverviewNews } from '@news/hooks/useOverviewNews';
import { useState } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import { useSearchParams } from 'react-router-dom';

export const NewsList = () => {
    const [searchParams, setSearchParams] = useSearchParams();
    const selectedTab = searchParams.get('tab') || 'latest';

    const intl = useIntl();

    const { latest, trending, isGetNewsError } = useOverviewNews();
    const { news, sentinelRef, hasRealItems, isLoadingMore } = selectedTab === 'latest' ? latest : trending;

    return (
        <>
            <StickyTabs
                value={selectedTab}
                showGradient={hasRealItems || isLoadingMore}
                onChange={(_event, newValue) => {
                    setSearchParams({ tab: newValue }, { preventScrollReset: true, replace: false });
                }}
                content={
                    <Grid2 container pb="24px" spacing="32px" position="relative" px={{ xs: 2, md: 4 }} py="24px">
                        {hasRealItems || isLoadingMore ? (
                            <>
                                {news.map((newsItem, index) =>
                                    isInfiniteLoadingItem(newsItem) ? (
                                        <Grid2 key={`skeleton-${index}`} size={{ xs: 12, sm: 6, md: 4, lg: 3 }}>
                                            <MediaStoryCard
                                                key={`news-skeleton-${index}`}
                                                image={{
                                                    url: '/static/img/backgrounds/LeftSplitImageDesktop1.png',
                                                    alt_text: intl.formatMessage({
                                                        id: 'news.loadingThumbnail',
                                                        defaultMessage: 'Loading news thumbnail',
                                                    }),
                                                }}
                                                baseUrl=""
                                                date="Loading"
                                                slug="123"
                                                title="Loading"
                                                loading
                                            />
                                        </Grid2>
                                    ) : (
                                        <Grid2 key={`news-${newsItem.id}`} size={{ xs: 12, sm: 6, md: 4, lg: 3 }}>
                                            <MediaStoryCard
                                                key={`news-${newsItem.id}`}
                                                baseUrl={`${APP_PREFIX_PATH}/news/`}
                                                slug={newsItem.id.toString()}
                                                image={{
                                                    url: newsItem.picture ?? '',
                                                    alt_text: intl.formatMessage(
                                                        { id: 'news.thumbnail', defaultMessage: 'Thumbnail for {title}' },
                                                        { title: newsItem.title }
                                                    ),
                                                }}
                                                date={newsItem.date}
                                                title={newsItem.title ?? ''}
                                                link={{
                                                    url: newsItem.id.toString(),
                                                    target: '_self',
                                                }}
                                                draft={!newsItem.published}
                                            />
                                        </Grid2>
                                    )
                                )}
                            </>
                        ) : isGetNewsError ? (
                            <CenteredEmptyState
                                message={
                                    <FormattedMessage
                                        id="news.latest.error"
                                        defaultMessage="An error occurred while fetching the latest news."
                                    />
                                }
                            />
                        ) : (
                            <CenteredEmptyState
                                message={
                                    <FormattedMessage
                                        id="news.latest.empty"
                                        defaultMessage="No news articles are available at the moment."
                                    />
                                }
                            />
                        )}
                        <div ref={sentinelRef} className="basis-full h-px" />
                    </Grid2>
                }
            >
                <Tabs.Tab value="latest" label={<FormattedMessage id="news.latest" defaultMessage="Latest News" />} />
                <Tabs.Tab
                    value="trending"
                    label={<FormattedMessage id="news.trending" defaultMessage="Trending News" />}
                />
            </StickyTabs>
        </>
    );
};
